import type { Endpoints } from '@xver/apis';

export const PRICING = {
    CPUSecondsRate: 0.0003,
    GiBSecondsRate: 0.00005,
    HTTPRequestsRate: 20.0,
    VolumeGBMonthlyRate: 4.0,
    FreeVCPUSeconds: 180_000,
    FreeGiBSeconds: 360_000,
    FreeHTTPRequests: 1_000_000,
    SECONDS_PER_MONTH: 2_628_000,
};

export type PricingValues = typeof PRICING;

export function pricingFromResponse(
    resp?: Endpoints.v1.Pricing.ProductsResponseModel
): PricingValues {
    if (!resp) return PRICING;

    const container = resp.products.container;
    return {
        CPUSecondsRate: container.rates.cpu_seconds,
        GiBSecondsRate: container.rates.gib_seconds,
        HTTPRequestsRate: container.rates.http_requests_per_million,
        // Falls back to the local default if the API predates the volume rate.
        VolumeGBMonthlyRate: container.rates.volume_gb_monthly_rate ?? PRICING.VolumeGBMonthlyRate,
        FreeVCPUSeconds: container.free_tier.vcpu_seconds,
        FreeGiBSeconds: container.free_tier.gib_seconds,
        FreeHTTPRequests: container.free_tier.http_requests,
        SECONDS_PER_MONTH: PRICING.SECONDS_PER_MONTH,
    };
}

export interface ConfigForEstimate {
    cpu: number;
    memory: number;
    min_replicas: number;
    max_replicas: number;
}

export interface MonthlyEstimate {
    minVCPUSeconds: number;
    maxVCPUSeconds: number;
    minGiBSeconds: number;
    maxGiBSeconds: number;
    minMonthlyCost: number;
    maxMonthlyCost: number;
    isInstanceBased: boolean;
}

export function estimateMonthly(
    config: ConfigForEstimate,
    pricingType: 'request_based' | 'instance_based',
    pricing: PricingValues = PRICING
): MonthlyEstimate {
    const vcpu = config.cpu / 1000;
    const gib = config.memory / 1024;

    const minVCPUSeconds = vcpu * config.min_replicas * pricing.SECONDS_PER_MONTH;
    const maxVCPUSeconds = vcpu * config.max_replicas * pricing.SECONDS_PER_MONTH;
    const minGiBSeconds = gib * config.min_replicas * pricing.SECONDS_PER_MONTH;
    const maxGiBSeconds = gib * config.max_replicas * pricing.SECONDS_PER_MONTH;

    const chargedVCPU = (sec: number) => Math.max(sec - pricing.FreeVCPUSeconds, 0);
    const chargedGiB = (sec: number) => Math.max(sec - pricing.FreeGiBSeconds, 0);

    const minMonthlyCost =
        chargedVCPU(minVCPUSeconds) * pricing.CPUSecondsRate +
        chargedGiB(minGiBSeconds) * pricing.GiBSecondsRate;
    const maxMonthlyCost =
        chargedVCPU(maxVCPUSeconds) * pricing.CPUSecondsRate +
        chargedGiB(maxGiBSeconds) * pricing.GiBSecondsRate;

    return {
        minVCPUSeconds,
        maxVCPUSeconds,
        minGiBSeconds,
        maxGiBSeconds,
        minMonthlyCost,
        maxMonthlyCost,
        isInstanceBased: pricingType === 'instance_based',
    };
}
